import React from "react"

import { useEvent } from "shared/lib/useEvent"

import { GlobalSearchSelectableOption } from "features/global-search/types/GlobalSearch"

export function useGlobalSearchKeyboardNavigation(
  itemRefs: React.RefObject<Map<number, HTMLButtonElement>>,
  optionRefs: React.RefObject<Map<number, GlobalSearchSelectableOption>>,
  activeIndex: number | null,
  activateItem: (index: number | null) => void,
  keyboardActiveRef: React.MutableRefObject<boolean>,
  onChange: (option: GlobalSearchSelectableOption | null) => void
): [
  onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement>,
  onContainerKeyDown: React.KeyboardEventHandler<HTMLDivElement>
] {
  const handleKeyDown = useEvent((e: React.KeyboardEvent) => {
    const count = itemRefs.current?.size ?? 0

    if (e.key === "ArrowDown") {
      if (count === 0) return
      e.preventDefault()
      activateItem(
        activeIndex === null ? 0 : Math.min(activeIndex + 1, count - 1)
      )
    } else if (e.key === "ArrowUp") {
      if (count === 0) return
      e.preventDefault()
      activateItem(
        activeIndex === null ? count - 1 : Math.max(activeIndex - 1, 0)
      )
    } else if (e.key === "Enter") {
      if (activeIndex === null) return
      e.preventDefault()
      const option = optionRefs.current?.get(activeIndex)
      if (option !== undefined) onChange(option)
    } else if (e.key === "Home") {
      if (count === 0) return
      e.preventDefault()
      activateItem(0)
    } else if (e.key === "End") {
      if (count === 0) return
      e.preventDefault()
      activateItem(count - 1)
    } else if (e.key === "Escape") {
      activateItem(null)
    }
  })

  const onInputKeyDown = useEvent((e: React.KeyboardEvent<HTMLInputElement>) =>
    handleKeyDown(e)
  )

  const onContainerKeyDown = useEvent(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // eslint-disable-next-line no-param-reassign
      keyboardActiveRef.current = true
      handleKeyDown(e)
    }
  )

  return [onInputKeyDown, onContainerKeyDown]
}
